import { MASTERED_AUDIO_REGISTRY } from './mastered-audio-registry.ts';

export type ProductionAudioBus = 'music' | 'ride' | 'wind' | 'environment' | 'ui' | 'reaction';

export interface ProductionAudioRegistryEntry {
  readonly id: string;
  readonly bus: ProductionAudioBus;
  readonly role: string;
  readonly source: 'project-authored-procedural' | 'recorded-candidate' | 'generated-candidate';
  readonly runtimeUrl: string | null;
  readonly creator: string;
  readonly generatorModelVersion: string | null;
  readonly prompt: string | null;
  readonly license: string;
  readonly subscriptionTier: string | null;
  readonly sourceHash: string | null;
  readonly finalHash: string | null;
  readonly processing: readonly string[];
  readonly inGameSafe: boolean;
  readonly socialExportSafe: boolean;
  readonly attribution: string | null;
  readonly status: 'shipping' | 'audition_required' | 'internal_test';
}

function entry(value: ProductionAudioRegistryEntry): ProductionAudioRegistryEntry {
  return Object.freeze({ ...value, processing: Object.freeze([...value.processing]) });
}

export const PRODUCTION_AUDIO_REGISTRY: readonly ProductionAudioRegistryEntry[] = Object.freeze([
  entry({
    id: 'procedural.ride-mechanics',
    bus: 'ride',
    role: 'wheel rhythm and semantic operation cues',
    source: 'project-authored-procedural',
    runtimeUrl: null,
    creator: 'Buncha Games',
    generatorModelVersion: 'Web Audio deterministic synthesis v1',
    prompt: null,
    license: 'Project-authored procedural audio',
    subscriptionTier: null,
    sourceHash: null,
    finalHash: null,
    processing: ['runtime gain, filtering and speed response'],
    inGameSafe: true,
    socialExportSafe: true,
    attribution: null,
    status: 'shipping',
  }),
  ...MASTERED_AUDIO_REGISTRY,
  entry({
    id: 'music.internal-builder-test',
    bus: 'music',
    role: 'QA-only loop for unlock, looping, ducking and teardown checks',
    source: 'project-authored-procedural',
    runtimeUrl: null,
    creator: 'Buncha Games',
    generatorModelVersion: 'internal-builder-test-loop v1',
    prompt: null,
    license: 'Project-authored procedural audio',
    subscriptionTier: null,
    sourceHash: null,
    finalHash: null,
    processing: ['runtime synthesis; excluded from production and exports'],
    inGameSafe: true,
    socialExportSafe: false,
    attribution: null,
    status: 'internal_test',
  }),
  entry({
    id: 'procedural.wind',
    bus: 'wind',
    role: 'speed-reactive ride wind',
    source: 'project-authored-procedural',
    runtimeUrl: null,
    creator: 'Buncha Games',
    generatorModelVersion: 'seeded-noise-loop v1',
    prompt: null,
    license: 'Project-authored procedural audio',
    subscriptionTier: null,
    sourceHash: null,
    finalHash: null,
    processing: ['runtime low-pass filtering and playback-rate response'],
    inGameSafe: true,
    socialExportSafe: true,
    attribution: null,
    status: 'shipping',
  }),
  entry({
    id: 'music.builder-workshop.pending-legacy',
    bus: 'music',
    role: '90–120 second seamless builder score',
    source: 'generated-candidate',
    runtimeUrl: null,
    creator: 'Pending founder-approved audition',
    generatorModelVersion: null,
    prompt: null,
    license: 'Pending license review',
    subscriptionTier: null,
    sourceHash: null,
    finalHash: null,
    processing: [],
    inGameSafe: false,
    socialExportSafe: false,
    attribution: null,
    status: 'audition_required',
  }),
]);

export function shippingProductionAudio(): readonly ProductionAudioRegistryEntry[] {
  return PRODUCTION_AUDIO_REGISTRY.filter(
    (asset) => asset.status === 'shipping' && asset.inGameSafe,
  );
}

export function socialExportProductionAudio(): readonly ProductionAudioRegistryEntry[] {
  return shippingProductionAudio().filter((asset) => asset.socialExportSafe);
}

export function validateShippingProductionAudio(
  entries: readonly ProductionAudioRegistryEntry[] = PRODUCTION_AUDIO_REGISTRY,
): readonly string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const asset of entries) {
    if (ids.has(asset.id)) errors.push(`${asset.id}: duplicate id`);
    ids.add(asset.id);
    if (asset.status !== 'shipping') continue;
    if (!asset.creator.trim()) errors.push(`${asset.id}: creator is required`);
    if (!asset.license.trim()) errors.push(`${asset.id}: license is required`);
    if (!asset.inGameSafe) errors.push(`${asset.id}: inGameSafe must be true`);
    if (asset.source !== 'project-authored-procedural') {
      if (!asset.runtimeUrl) errors.push(`${asset.id}: runtimeUrl is required`);
      if (!asset.sourceHash) errors.push(`${asset.id}: sourceHash is required`);
      if (!asset.finalHash) errors.push(`${asset.id}: finalHash is required`);
    }
  }
  return errors;
}
